import { Icon, Skeleton, Tooltip } from '@sendible/design-system/src';
import { getAvatarInitials, getColorTone } from '@sendible/design-system/src/components/utils/helpers';
import { useTranslation } from 'react-i18next';
import { useBridgeUser } from '@sendible/shared-state-bridge';
import { EVENTS, publish } from '@sendible/event-manager';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useGetMessage, useResolveRecipients } from '@sendible/frontendv2/src/models/calendar';
import { PdfThumbnail } from '@sendible/frontendv2/src/components/MediaSystem/components/MediaBelt/PdfThumbnail';
import { MediaPreview } from '@sendible/frontendv2/src/components/MediaPreview';
import {
  CaptionText,
  Content,
  FilterTab,
  FilterTabs,
  InitialsAvatar,
  CampaignRow,
  MarkAllButton,
  MediaGrid,
  MediaOverflow,
  OverflowMask,
  MediaThumb,
  ThumbHighlight,
  NetworkIconsList,
  ProfilesSectionHeader,
  ProfilesToggleButton,
  PropertyLabel,
  PropertyRow,
  PropertyValue,
  Section,
  SectionBody,
  SectionHeader,
  StatusIconWrapper,
  Container,
  ErrorBox,
} from './index.styles';
import { RecipientCard } from '../../../RecipientCard';
import { RecipientsList } from '../../index.styles';
import { useCalendarContext, useCalendarSidebarContext } from '../../../../context';
import {
  formatSendDate,
  fullDateToMonthString,
  getBounceStatus,
  getCalendarCardMedias,
  getChannelIcon,
  getDeliveryStatusIcon,
  getDeliveryStatusLabel,
  getDeliveryStatusTooltip,
  getFailureReasonMessage,
  getIsPartialModeSuccess,
  getRecurrenceLabel,
  isPdfMedia,
  processTags,
} from '../../../../utils';
import { useCalendarMediaList, useRenderWithLinks } from '../../../../hooks';
import { ProgressIndicator } from '../../../ProgressIndicator';

const MEDIA_VISIBLE_COUNT = 3;

export const MessagesSidebarContent = () => {
  const [isPropertiesOpen, setIsPropertiesOpen] = useState(true);
  const [isProfilesOpen, setIsProfilesOpen] = useState(true);
  const [recipientFilterState, setRecipientFilterState] = useState<{ messageId: CalendarMessage['id']; value: 'all' | 'sent' | 'failed' }>({
    messageId: 0,
    value: 'all',
  });
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [isCaptionScrollable, setIsCaptionScrollable] = useState(false);
  const captionRef = useRef<HTMLParagraphElement>(null);
  const user = useBridgeUser();
  const { closeSidebar, filterParams } = useCalendarContext();
  const { sidebarMessageId } = useCalendarSidebarContext();
  const { t, i18n } = useTranslation('calendar');
  const { data: message, isLoading } = useGetMessage(sidebarMessageId);
  const resolveRecipients = useResolveRecipients();
  const previewMediaList = useCalendarMediaList(getCalendarCardMedias(message?.body), previewIndex !== null);
  const renderWithLinks = useRenderWithLinks();

  const isWorkspaceMode = Boolean(user?.isWorkspaceMode);
  const hasFailureReason = Boolean(message?.deliveryStatus.failureReason);
  const failureReasonMessageKey = getFailureReasonMessage(message?.deliveryStatus.failureReason ?? '');
  const failureReasonMessage = failureReasonMessageKey ? t(failureReasonMessageKey) : '';

  const recipientFilter = recipientFilterState.messageId === sidebarMessageId ? recipientFilterState.value : 'all';

  const handleRecipientFilterChange = (value: 'all' | 'sent' | 'failed') => {
    setRecipientFilterState({ messageId: sidebarMessageId ?? 0, value });
  };

  useEffect(() => {
    const el = captionRef.current;

    if (el) {
      setIsCaptionScrollable(el.scrollHeight > el.clientHeight);
    } else {
      setIsCaptionScrollable(false);
    }
  }, [message?.body?.messageText]);

  if (isLoading && !message) {
    return (
      <Content>
        <Skeleton
          height="20rem"
          width="100%"
        />
        <Skeleton
          height="10rem"
          width="100%"
        />
      </Content>
    );
  }

  if (!message) return null;

  const { status, workspace, body, destinations, sendDateLocalized, author } = message;
  const media = getCalendarCardMedias(body);
  const visibleMedia = media.slice(0, MEDIA_VISIBLE_COUNT);
  const overflowCount = media.length > MEDIA_VISIBLE_COUNT ? media.length - MEDIA_VISIBLE_COUNT : 0;
  const overflowPreviewMedia = overflowCount > 0 ? media[MEDIA_VISIBLE_COUNT] : null;
  const clockFormat = user?.clockFormat ?? 12;
  const captionText = body?.messageText ?? '';
  const networksByType = destinations.reduce<Map<string, string[]>>((acc, d) => {
    const names = acc.get(d.channelType) ?? [];

    if (!names.includes(d.channelName)) names.push(d.channelName);
    acc.set(d.channelType, names);

    return acc;
  }, new Map());
  const isPartialModeSuccess = getIsPartialModeSuccess(filterParams);
  const deliveryStatusIcon = getDeliveryStatusIcon(status, !!message.recurrence, message.deliveryStatus, isPartialModeSuccess);
  const { isPartiallyBounced } = getBounceStatus(message.deliveryStatus);
  const recipients = message.deliveryStatus.recipients ?? [];
  const sentRecipients = recipients.filter((r) => r.status === 'Sent');
  const failedRecipients = recipients.filter((r) => r.status !== 'Sent');
  const resolvableRecipients = failedRecipients.filter((r) => !r.isResolved);
  const filterMap: Record<'all' | 'sent' | 'failed', CalendarRecipient[]> = { all: recipients, sent: sentRecipients, failed: failedRecipients };
  const filteredRecipients = filterMap[recipientFilter];
  const isAssignedToMe = status === 'ForApproval' && message.task?.assignedTo?.id === user?.userId;

  const workspaceData = isWorkspaceMode ? author || workspace : workspace;

  const statusTooltip = getDeliveryStatusTooltip({
    recurrence: message.recurrence,
    deliveryStatus: message.deliveryStatus,
    status,
    isPartialModeSuccess,
    t,
    language: i18n.language,
  });

  const statusLabel = getDeliveryStatusLabel({
    deliveryStatus: message.deliveryStatus,
    status,
    isPartialModeSuccess,
    isAssignedToMe,
    t,
  });

  const handleDownload = async (action: string, mediaId: MediaIdType) => {
    if (action !== 'download') return;

    const item = previewMediaList.find((m) => m.id === mediaId);

    if (!item) return;

    const response = await fetch(item.downloadUrl);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const tempLink = document.createElement('a');

    tempLink.href = blobUrl;
    tempLink.setAttribute('download', item.name);
    tempLink.click();
    tempLink.remove();
    URL.revokeObjectURL(blobUrl);
  };

  return (
    <Container>
      {hasFailureReason && (
        <ErrorBox>
          <p>{failureReasonMessage}</p>
        </ErrorBox>
      )}
      <Content>
        <Section>
          <SectionHeader
            type="button"
            onClick={() => setIsPropertiesOpen((v) => !v)}
            aria-expanded={isPropertiesOpen}
            $isOpen={isPropertiesOpen}
          >
            <span>{t('properties')}</span>
            <Icon
              name="chevron_right"
              color="brandDark"
            />
          </SectionHeader>
          {isPropertiesOpen && (
            <SectionBody>
              <PropertyRow>
                <PropertyLabel>{t('statuses')}</PropertyLabel>
                <PropertyValue>
                  {deliveryStatusIcon &&
                    (isPartiallyBounced ? (
                      <Tooltip
                        noDecoration
                        content={statusTooltip}
                        maxWidth
                      >
                        <StatusIconWrapper>
                          <Icon
                            name={deliveryStatusIcon.icon}
                            color={deliveryStatusIcon.color}
                            strokeWidth={1.8}
                          />
                          {deliveryStatusIcon.progress && (
                            <ProgressIndicator
                              percent={deliveryStatusIcon.progress.percent}
                              type={deliveryStatusIcon.progress.type}
                            />
                          )}
                        </StatusIconWrapper>
                      </Tooltip>
                    ) : (
                      <Icon
                        name={deliveryStatusIcon.icon}
                        color={deliveryStatusIcon.color}
                        strokeWidth={1.8}
                      />
                    ))}
                  <span>{statusLabel}</span>
                </PropertyValue>
              </PropertyRow>
              {message.queue && (
                <PropertyRow>
                  <PropertyLabel>{t('queues')}</PropertyLabel>
                  <PropertyValue>
                    <span>{message.queue.name}</span>
                  </PropertyValue>
                </PropertyRow>
              )}
              {status === 'ForApproval' && message.task?.assignedTo && (
                <PropertyRow>
                  <PropertyLabel>{t('assigned_to')}</PropertyLabel>
                  <PropertyValue>
                    <span>{message.task.assignedTo.name}</span>
                  </PropertyValue>
                </PropertyRow>
              )}
              {message.recurrence && (
                <PropertyRow>
                  <PropertyLabel>{t('recurrence')}</PropertyLabel>
                  <PropertyValue>
                    <span>{getRecurrenceLabel(message.recurrence, t, i18n.language)}</span>
                  </PropertyValue>
                </PropertyRow>
              )}
              {!message.recurrence && (
                <PropertyRow>
                  <PropertyLabel>{t('send_date')}</PropertyLabel>
                  <PropertyValue>
                    <span>{formatSendDate(sendDateLocalized, user?.language ?? 'en', clockFormat)}</span>
                  </PropertyValue>
                </PropertyRow>
              )}
              {message.campaigns && message.campaigns.length > 0 && (
                <PropertyRow>
                  <PropertyLabel>{t('campaigns')}</PropertyLabel>
                  <PropertyValue $column>
                    {message.campaigns.map((campaign) => (
                      <CampaignRow key={campaign.id}>
                        <InitialsAvatar
                          $color={campaign.color}
                          $isDark={getColorTone(campaign.color) === 'dark'}
                        />
                        <span>{campaign.name}</span>
                      </CampaignRow>
                    ))}
                  </PropertyValue>
                </PropertyRow>
              )}
              <PropertyRow>
                <PropertyLabel>{t('author')}</PropertyLabel>
                <PropertyValue>
                  <InitialsAvatar
                    $color={workspaceData.color}
                    $isDark={getColorTone(workspaceData.color) === 'dark'}
                  >
                    <span>{getAvatarInitials(workspaceData.name)}</span>
                  </InitialsAvatar>
                  <span>{workspaceData.name}</span>
                </PropertyValue>
              </PropertyRow>
              {captionText && (
                <PropertyRow>
                  <PropertyLabel>{t('caption')}</PropertyLabel>
                  <PropertyValue>
                    <CaptionText
                      ref={captionRef}
                      $scrollable={isCaptionScrollable}
                    >
                      {renderWithLinks(processTags(captionText))}
                    </CaptionText>
                  </PropertyValue>
                </PropertyRow>
              )}
              {media.length > 0 && (
                <PropertyRow>
                  <PropertyLabel>{t('attachments')}</PropertyLabel>
                  <PropertyValue>
                    <MediaGrid>
                      {visibleMedia.map((m, index) => (
                        <MediaThumb
                          key={m.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setPreviewIndex(index)}
                          onKeyDown={(e) => e.key === 'Enter' && setPreviewIndex(index)}
                        >
                          {isPdfMedia(m) ? (
                            <PdfThumbnail
                              url={m.url}
                              altText=""
                            />
                          ) : (
                            <img
                              src={m.type === 'Video' && m.thumbnailUrl ? m.thumbnailUrl : m.url}
                              alt=""
                            />
                          )}
                          {(isPdfMedia(m) || m.isLinkPreview || m.type === 'Video' || m.type === 'Gif') && (
                            <ThumbHighlight>
                              {m.isLinkPreview ? (
                                <Icon
                                  name="link"
                                  color="brandLight"
                                  strokeWidth={1.8}
                                />
                              ) : m.type === 'Gif' ? (
                                <span>GIF</span>
                              ) : (
                                <Icon
                                  name={m.type === 'Video' ? 'play' : 'file'}
                                  color="brandLight"
                                  strokeWidth={1.8}
                                />
                              )}
                            </ThumbHighlight>
                          )}
                        </MediaThumb>
                      ))}
                      {overflowCount > 0 && (
                        <MediaOverflow
                          role="button"
                          tabIndex={0}
                          onClick={() => setPreviewIndex(MEDIA_VISIBLE_COUNT)}
                          onKeyDown={(e) => e.key === 'Enter' && setPreviewIndex(MEDIA_VISIBLE_COUNT)}
                        >
                          {overflowPreviewMedia && (
                            <img
                              src={
                                overflowPreviewMedia.type === 'Video' && overflowPreviewMedia.thumbnailUrl
                                  ? overflowPreviewMedia.thumbnailUrl
                                  : overflowPreviewMedia.url
                              }
                              alt=""
                            />
                          )}
                          <OverflowMask>+{overflowCount}</OverflowMask>
                        </MediaOverflow>
                      )}
                    </MediaGrid>
                  </PropertyValue>
                </PropertyRow>
              )}
              {networksByType.size > 0 && (
                <PropertyRow>
                  <PropertyLabel>{t('networks_plural')}</PropertyLabel>
                  <PropertyValue>
                    <NetworkIconsList>
                      {Array.from(networksByType.entries()).map(([channelType, names]) => {
                        const icon = getChannelIcon(channelType as MessageChannels);

                        return icon ? (
                          <Tooltip
                            key={channelType}
                            noDecoration
                            content={names.map((name, i) => (
                              <Fragment key={name}>
                                {name}
                                {i < names.length - 1 && <br />}
                              </Fragment>
                            ))}
                          >
                            <Icon name={icon} />
                          </Tooltip>
                        ) : null;
                      })}
                    </NetworkIconsList>
                  </PropertyValue>
                </PropertyRow>
              )}
            </SectionBody>
          )}
        </Section>
        {recipients.length > 0 && (
          <Section>
            <ProfilesSectionHeader>
              <ProfilesToggleButton
                type="button"
                onClick={() => setIsProfilesOpen((v) => !v)}
                $isOpen={isProfilesOpen}
                aria-expanded={isProfilesOpen}
              >
                <span>{t('profiles_count', { count: recipients.length })}</span>
                <Icon
                  name="chevron_right"
                  color="brandDark"
                />
              </ProfilesToggleButton>
              {resolvableRecipients.length > 0 && (
                <MarkAllButton
                  type="button"
                  onClick={() => resolveRecipients.mutate({ recipientIds: resolvableRecipients.map((r) => r.id), messageId: message.id })}
                >
                  {t('mark_all_as_read', { count: resolvableRecipients.length })}
                </MarkAllButton>
              )}
            </ProfilesSectionHeader>
            {isProfilesOpen && (
              <SectionBody>
                {failedRecipients.length > 0 && (
                  <FilterTabs>
                    <FilterTab
                      type="button"
                      $active={recipientFilter === 'all'}
                      onClick={() => handleRecipientFilterChange('all')}
                    >
                      {t('filter_all')}
                    </FilterTab>
                    <FilterTab
                      type="button"
                      $active={recipientFilter === 'sent'}
                      onClick={() => handleRecipientFilterChange('sent')}
                    >
                      {`${t('Sent')} (${sentRecipients.length})`}
                    </FilterTab>
                    <FilterTab
                      type="button"
                      $active={recipientFilter === 'failed'}
                      onClick={() => handleRecipientFilterChange('failed')}
                    >
                      {`${t('Failed')} (${failedRecipients.length})`}
                    </FilterTab>
                  </FilterTabs>
                )}
                <RecipientsList data-testid="calendar-right-sidebar-recipient-list">
                  {filteredRecipients.map((recipient) => {
                    const networkIcon = getChannelIcon(recipient.network as MessageChannels);

                    return (
                      <RecipientCard
                        key={recipient.id}
                        id={recipient.id}
                        name={recipient.name}
                        networkIcon={networkIcon}
                        networkUrl={recipient.avatarUrl}
                        errorMessage={recipient.errorMessage}
                        isSuccess={recipient.status === 'Sent'}
                        isResolved={recipient.isResolved}
                        isLoading={resolveRecipients.isPending && (resolveRecipients.variables?.recipientIds?.includes(recipient.id) ?? false)}
                        onResolve={() => resolveRecipients.mutate({ recipientIds: [recipient.id], messageId: message.id })}
                        onRepost={() => {
                          publish(EVENTS.OPEN_COMPOSE, {
                            mode: 'repost',
                            id: message.id,
                            date: fullDateToMonthString(sendDateLocalized),
                            offset: user?.timezone ?? 0,
                            recipientId: recipient.profileId,
                          });
                          closeSidebar();
                        }}
                      />
                    );
                  })}
                </RecipientsList>
              </SectionBody>
            )}
          </Section>
        )}
        {previewIndex !== null && previewMediaList.length > 0 && (
          <MediaPreview
            initialMediaIndex={previewIndex}
            mediaList={previewMediaList}
            supportedActions={['download']}
            onActionClick={handleDownload}
            onClose={() => setPreviewIndex(null)}
            shouldShowActions
          />
        )}
      </Content>
    </Container>
  );
};
